import React, { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, InputNumber, Select, Tooltip } from 'antd';
import { ECalculationType, EMetricTypes, EOccurrenceType, EPairType } from '@src/emp-3.0/types';
import styles from './styles.less';
import { EMetricType } from '@src/pages/metric-templates/types';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  occurrenceTypeOptions,
  pairingLogicOptions,
  withinUnitOptions,
} from '@src/pages/metric-templates/constants';
import { useGetMetricSupportedUnits } from '@src/emp-3.0/api/queryHooks';
import AttributeSelect from '@src/emp-3.0/components/custom-dimension/component/NamedSelectWithExtras/AttributeSelect';
import { SelectInView } from '@src/emp-3.0/components/Metrics/IndividualMetric/MetricBuilder/styled';
import moment from 'moment';
import { useGetEventNameById } from '@hooks/useGetEventNameById';
import useFieldRequiredStatus from '@src/pages/workflow/hooks/useFieldRequiredStatus';
import { checkSuccessConversionWindow, valueInSeconds } from '@src/emp-3.0/utils/metrics';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { EFormMode } from '@src/emp-3.0/types/common';
import { isViewMode } from '@src/pages/metric-templates/utils';
import {
  FormSettingRow,
  FormItemViewMode,
  TimeoutViewMode,
} from '../../Flow/FlowForm/FlowSettings';
import { StyledSettingWrapper } from '../../Flow/FlowForm/styled';
import WithoutEventMetric from './WithoutEventMetric';
import PropogateDimension from '../../Flow/FlowForm/PropogateDimension/PropogateDimension';

// is_higher_better
export const qualityIndicatorOptions = [
  {
    value: true,
    label: (
      <span>
        <span style={{ color: '#52C41A' }}>Higher</span>
        <span> is better</span>
      </span>
    ),
  },
  {
    value: false,
    label: (
      <span>
        <span style={{ color: '#52C41A' }}>Lower</span>
        <span> is better</span>
      </span>
    ),
  },
];

const getOccurrenceTypeOptions = (type: EMetricType) => {
  return occurrenceTypeOptions.filter((x) => x.type === type) || [];
};

const UnitSelection: React.FC<{
  value?: number;
  onChange?: (value: string | number | null) => void;
  disabled?: boolean;
}> = (props) => {
  const { disabled } = props;
  // Ant Design will automatically inject those props into the component
  // do not need to pass explicitly through the parent.

  const [value, setValue] = useState<string | null>();
  const [category, setCategory] = useState<string>('No Unit');
  const { data: units, isFetching } = useGetMetricSupportedUnits();

  const categoryOption: any[] = useMemo(() => {
    const categoryMap = {};
    units?.forEach((unit) => {
      categoryMap[unit.category] = {
        label: unit.category,
        value: unit.category,
      };
    });

    // Add No unit option
    categoryMap['No Unit'] = {
      label: 'No Unit',
      value: 'No Unit',
    };

    return Object.values(categoryMap);
  }, [units]);

  const unitOption: any[] = useMemo(() => {
    const unitArray: any = [];
    units?.forEach((unit) => {
      if (unit.category === category) {
        unitArray.push({
          label: unit.name,
          value: unit.name,
        });
      }
    });
    return unitArray;
  }, [category, units]);

  // Transform array value to API number value
  useEffect(() => {
    if (value !== undefined && props.onChange) {
      props.onChange(value);
    }
  }, [value]); // Please don't add props.onChange to dependencies will trigger loop render.

  // Convert API number value to Cascader array value
  useEffect(() => {
    if (props.value) {
      const unit = units?.find((u) => u.name === props.value);
      if (unit) {
        setValue(unit.name);
        setCategory(unit.category);
      }
    }
  }, [units, props.value]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span>Display Unit</span>
      <Select
        style={{ minWidth: '100px', margin: '0 8px', width: 'fit-content' }}
        dropdownStyle={{ minWidth: '130px' }}
        onChange={(value) => {
          setCategory(value);
          setValue(null);
        }}
        options={categoryOption}
        value={category}
        loading={isFetching}
        disabled={disabled}
      />
      <Select
        style={{ minWidth: '100px', margin: '0 8px', width: 'fit-content' }}
        dropdownStyle={{ minWidth: '150px' }}
        showSearch
        disabled={category === 'No Unit' || disabled}
        onChange={(value) => setValue(value)}
        options={unitOption}
        value={value}
        loading={isFetching}
      />
    </div>
  );
};

export const calculateOptions = [
  {
    value: ECalculationType.CONVERSION_RATE,
    label: 'Conversion Rate',
  },
  {
    value: ECalculationType.NUMBER,
    label: 'Conversion Count',
  },
];
export const CalculateTypeOptions = [
  {
    label: 'Count',
    value: 'count',
  },
  {
    label: 'Total values',
    value: 'sum',
  },
  {
    label: 'Average values',
    value: 'average',
  },
];

interface ErrorMsgOfTimeCheck {
  complete_timeout?: string;
  max_value?: string;
}

const MetricDefinition = ({ form, metric }) => {
  const metricType = Form.useWatch('metrics_type', form);
  const conversionOccurrenceType = Form.useWatch(['definition', 'occurrence_type'], form);
  const calculation = Form.useWatch(['definition', 'calculation'], form);
  const singleEventId = Form.useWatch(['definition', 'event'], form);
  const attributeType = Form.useWatch(['definition', 'attribute_type'], form);
  const distribution = Form.useWatch(['definition', 'distribution'], form);
  const pairingLogic = Form.useWatch(['definition', 'pairing_logic'], form);
  const minValue = Form.useWatch(['definition', 'distribution', 'min_value'], form);
  const maxValue = Form.useWatch(['definition', 'distribution', 'max_value'], form);
  const watchedTimeoutUnit = Form.useWatch(['definition', 'complete_timeout_unit'], form);
  const watchedCompleteTimeout = Form.useWatch(['definition', 'complete_timeout'], form);
  const startEvent = Form.useWatch(['definition', 'first_event'], form);
  const successEvent = Form.useWatch(['definition', 'second_event'], form);
  const { formMode } = useWorkflowStore();
  const isView = isViewMode(formMode);

  const { status: minStatus, enableRequired } = useFieldRequiredStatus([
    'definition',
    'distribution',
    'min_value',
  ]);
  const { status: maxStatus } = useFieldRequiredStatus(['definition', 'distribution', 'max_value']);
  const { status: rangeStatus } = useFieldRequiredStatus(['definition', 'within_range']);
  const { status: higherStatus } = useFieldRequiredStatus(['definition', 'is_higher_better']);
  const { status: attributeStatus } = useFieldRequiredStatus(['definition', 'attribute']);
  const { status: unitStatus } = useFieldRequiredStatus(['definition', 'unit']);

  // for get the event name by id
  const { getMappedEventNameById } = useGetEventNameById();

  const unit = metric?.definition?.unit;
  const isEverDeployed = formMode === EFormMode.EDIT && metric?.is_ever_deployed;

  const last14days = useMemo(
    () => [moment().subtract(13, 'days').startOf('day'), moment().subtract(0, 'day').endOf('day')],
    [],
  );

  const singleEventName = useMemo(() => {
    return getMappedEventNameById(singleEventId);
  }, [singleEventId, getMappedEventNameById]);

  const showNeedUnitCheckBox = calculation === 'sum' || calculation === 'average';

  const eventIds = useMemo(() => {
    const ids: number[] = [];
    if (startEvent && typeof startEvent === 'number') {
      ids.push(startEvent);
    }
    if (successEvent && typeof successEvent === 'number') {
      ids.push(successEvent);
    }
    return ids;
  }, [startEvent, successEvent]);

  /**
   * check time properties only for Duration metricType:
   * max value of complete_timeout is one day
   * complete_timeout value should be larger than timeout value and max value of distribution
   */
  const getErrorMsgOfTimeCheck = useCallback<() => Promise<ErrorMsgOfTimeCheck>>(async () => {
    const result: ErrorMsgOfTimeCheck = {};
    if (metricType !== EMetricTypes.DURATION) {
      return result;
    }
    const fieldsValue = form.getFieldsValue();
    const {
      complete_timeout,
      complete_timeout_unit,
      distribution: { max_value } = {},
    } = fieldsValue?.definition ?? {};
    const completeTimeoutValue = valueInSeconds({
      value: complete_timeout,
      unit: complete_timeout_unit,
    });
    if (completeTimeoutValue !== null) {
      try {
        await checkSuccessConversionWindow(complete_timeout_unit, complete_timeout, 6);
      } catch (msg) {
        result.complete_timeout = msg as string;
      }
      if (max_value !== undefined && completeTimeoutValue < max_value) {
        result.max_value = `Duration distribution range should be less than or equal to the duration limit`;
        result.complete_timeout =
          result.complete_timeout ??
          `Duration limit should be greater than or equal to the duration distribution range`;
      }
    }
    return result;
  }, [form, metricType]);

  useEffect(() => {
    const needDistribution = minValue != null && maxValue != null ? 1 : 0;
    const definition = form.getFieldValue('definition') || {};

    if (definition.need_distribution !== needDistribution) {
      form.setFieldsValue({
        definition: {
          ...definition,
          need_distribution: needDistribution,
        },
      });
    }
  }, [minValue, maxValue, form]);

  const renderItemForViewMode = (form, item, options?) => {
    const getFieldValue = (fieldPath) => form.getFieldValue(fieldPath);
    const renderSelectInView = (value, style = {}) => (
      <SelectInView style={style}>{value}</SelectInView>
    );

    let value;
    let text;
    const metricsType = getFieldValue('metrics_type');

    switch (item) {
      case 'min_value':
      case 'max_value':
        value = getFieldValue(['definition', 'distribution', item]);
        return renderSelectInView(value);

      case 'within_range':
        value = getFieldValue(['definition', 'within_range']);
        return (
          <div style={{ marginTop: '-1px', display: 'inline-block', minWidth: '20px' }}>
            {renderSelectInView(value)}
          </div>
        );

      case 'is_higher_better':
        value = getFieldValue(['definition', item]);
        text = options?.find((option) => option.value === value)?.label;
        if (metricsType === EMetricTypes.DURATION) {
          return renderSelectInView(text || value, { background: 'transparent' });
        }
        return renderSelectInView(text || value);

      default:
        value = getFieldValue(['definition', item]);
        text = options?.find((option) => option.value === value)?.label;
        return renderSelectInView(text || value);
    }
  };

  const { status: completeTimeoutStatus } = useFieldRequiredStatus([
    'definition',
    'complete_timeout',
  ]);

  const MetricValueIndicator = ({ mode, form }) => {
    return (
      <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '8px' }}>For metric improvement, </span>
        <Form.Item
          name={['definition', 'is_higher_better']}
          required={false}
          rules={enableRequired ? [{ required: true, message: 'Required!' }] : []}
          validateStatus={higherStatus}
          style={{ margin: 0 }}
        >
          {isView ? (
            renderItemForViewMode(form, 'is_higher_better', qualityIndicatorOptions)
          ) : (
            <Select
              placeholder="Please select..."
              style={{ minWidth: '80px' }}
              options={qualityIndicatorOptions}
              dropdownStyle={{
                minWidth: 'fit-content',
              }}
            />
          )}
        </Form.Item>
        <Tooltip
          overlayInnerStyle={{ width: 500 }}
          title={
            <>
              <div>Select ‘Higher is better’ if a higher number indicates a positive outcome.</div>
              <div>Conversely, Select ’Lower is better” if a lower number is more favorable.</div>
            </>
          }
        >
          <InfoCircleOutlined style={{ opacity: '0.4', fontSize: '18px', marginLeft: '8px' }} />
        </Tooltip>
      </div>
    );
  };

  if (metricType === EMetricTypes.DURATION) {
    return (
      <div style={{ paddingLeft: '28px' }}>
        <div className={styles.flexAlignCenter}>
          <span>Calculate Average/Percentile Duration of</span>
          {/*calculation default is average*/}
          <Form.Item noStyle hidden name={['definition', 'calculation']}>
            <Input />
          </Form.Item>
          <Form.Item noStyle name={['definition', 'pairing_logic']}>
            {isView ? (
              renderItemForViewMode(form, 'pairing_logic', pairingLogicOptions)
            ) : (
              <Select
                options={pairingLogicOptions}
                style={{ minWidth: '140px', margin: '0 8px' }}
              />
            )}
          </Form.Item>
          <Form.Item noStyle dependencies={[['definition', 'pairing_logic']]}>
            {({ getFieldValue }) => {
              let text;
              if (pairingLogic === EPairType.FIRST_FIRST) {
                text = 'first';
              }
              if (pairingLogic === EPairType.LAST_FIRST) {
                text = 'last';
              }
              const title = text ? (
                <span>
                  {`Pair the ${text}`}
                  <span style={{ fontStyle: 'italic' }}> initial event </span>
                  and the first
                  <span style={{ fontStyle: 'italic' }}> complete event </span>
                </span>
              ) : null;
              return (
                <Tooltip title={title}>
                  <InfoCircleOutlined
                    style={{ opacity: '0.4', fontSize: '18px', marginRight: 8 }}
                  />
                </Tooltip>
              );
            }}
          </Form.Item>
          <span>and count of</span>
          <Form.Item noStyle name={['definition', 'occurrence_type']}>
            {isView ? (
              renderItemForViewMode(
                form,
                'occurrence_type',
                getOccurrenceTypeOptions(EMetricType.TIME_INTERVAL),
              )
            ) : (
              <Select
                style={{ minWidth: '80px', margin: '0 8px' }}
                dropdownMatchSelectWidth={160}
                options={getOccurrenceTypeOptions(EMetricType.TIME_INTERVAL)}
              />
            )}
          </Form.Item>
        </div>
        <div
          className={styles.flexAlignCenter}
          style={{ marginLeft: -28, marginTop: 16, flexWrap: 'nowrap' }}
        >
          <FormSettingRow label="Maximum duration limit" required={false}>
            <Tooltip title="Define the duration of the metric">
              <InfoCircleOutlined
                style={{ opacity: '0.4', fontSize: '18px', alignSelf: 'baseline', paddingTop: 8 }}
              />
            </Tooltip>
            <StyledSettingWrapper>
              <Form.Item
                name={['definition', 'complete_timeout']}
                dependencies={[
                  ['definition', 'complete_timeout_unit'],
                  ['definition', 'distribution', 'max_value'],
                ]}
                validateStatus={completeTimeoutStatus}
                rules={
                  [
                    { required: true, message: 'Required' },
                    {
                      async validator() {
                        const { complete_timeout: errMsg } = await getErrorMsgOfTimeCheck();
                        if (errMsg) {
                          return Promise.reject(errMsg);
                        }
                      },
                    },
                  ].filter(Boolean) as React.ComponentProps<typeof Form.Item>['rules']
                }
                style={{ margin: 0 }}
              >
                {isView ? (
                  <SelectInView>{watchedCompleteTimeout?.toString()}</SelectInView>
                ) : (
                  <InputNumber min={1} style={{ width: 80 }} />
                )}
              </Form.Item>
              <Form.Item
                name={['definition', 'complete_timeout_unit']}
                rules={[{ required: true }]}
                style={{ margin: 0 }}
              >
                {isView ? (
                  <span>
                    {withinUnitOptions.find((item) => item.value === watchedTimeoutUnit)?.label}
                  </span>
                ) : (
                  <Select options={withinUnitOptions} style={{ width: '102px' }} />
                )}
              </Form.Item>
            </StyledSettingWrapper>
          </FormSettingRow>
        </div>
        <MetricValueIndicator mode="view" form={form} />
        <div style={{ marginTop: 5, display: 'flex', alignItems: 'flex-start' }}>
          <Form.Item name={['definition', 'need_distribution']} noStyle />
          <div style={{ marginTop: 5, display: 'inline-flex' }}>
            <span>Distribution Range:</span>
            <span style={{ color: 'rgba(0, 0, 0, 0.45)', marginLeft: 4 }}>(Optional)</span>
          </div>

          <div
            className={styles.flexAlignCenter}
            style={{ marginBottom: 5, display: 'inline-flex', marginLeft: 5 }}
          >
            <div style={{ margin: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={['definition', 'distribution', 'min_value']}
                  validateStatus={minStatus}
                  dependencies={[['definition', 'distribution', 'max_value']]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const maxValue = getFieldValue(['definition', 'distribution', 'max_value']);

                        if (
                          !enableRequired &&
                          (value === null ||
                            value === undefined ||
                            maxValue === null ||
                            maxValue === undefined)
                        ) {
                          return Promise.resolve();
                        }

                        if (value === null || value === undefined) {
                          return Promise.reject(new Error('Min value is required'));
                        }

                        if (maxValue !== null && maxValue !== undefined && value >= maxValue) {
                          return Promise.reject(new Error('Min value must be less than max value'));
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  {isView ? (
                    <SelectInView>{minValue}</SelectInView>
                  ) : (
                    <InputNumber
                      placeholder="min"
                      style={{ width: 60, margin: '0 5px' }}
                      controls={false}
                      min={0}
                    />
                  )}
                </Form.Item>
                <span style={{ marginTop: 6 }}>-</span>
                <Form.Item
                  style={{ marginBottom: 0 }}
                  name={['definition', 'distribution', 'max_value']}
                  validateStatus={maxStatus}
                  dependencies={[
                    ['definition', 'complete_timeout'],
                    ['definition', 'complete_timeout_unit'],
                    ['definition', 'distribution', 'min_value'],
                  ]}
                  rules={[
                    ({ getFieldValue }) => ({
                      async validator(_, value) {
                        const { max_value: errMsg } = await getErrorMsgOfTimeCheck();
                        if (errMsg) {
                          return Promise.reject(errMsg);
                        }
                        const minValue = getFieldValue(['definition', 'distribution', 'min_value']);
                        if (
                          !enableRequired &&
                          (value === null ||
                            value === undefined ||
                            minValue === null ||
                            minValue === undefined)
                        ) {
                          return Promise.resolve();
                        }

                        if (value === null || value === undefined) {
                          return Promise.reject(new Error('Max value is required'));
                        }

                        if (minValue !== null && minValue !== undefined && value <= minValue) {
                          return Promise.reject(
                            new Error('Max value must be greater than min value'),
                          );
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  {isView ? (
                    <SelectInView>{maxValue}</SelectInView>
                  ) : (
                    <InputNumber
                      placeholder="max"
                      style={{ width: 60, margin: '0 5px' }}
                      controls={false}
                      min={0}
                    />
                  )}
                </Form.Item>
                <span style={{ color: 'rgba(0, 0, 0, 0.65)', marginTop: 6 }}>seconds</span>
              </div>
            </div>
          </div>
        </div>
        <WithoutEventMetric form={form} isView={isView} />
        <PropogateDimension eventIds={eventIds} form={form} isView={isView} type={'Metric'} />
      </div>
    );
  }

  if (metricType === EMetricTypes.CONVERSION) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '28px' }}>
        <div className={styles.flexAlignCenter}>
          <span>Calculate</span>
          <Form.Item noStyle name={['definition', 'calculation']}>
            {isView ? (
              renderItemForViewMode(form, 'calculation', calculateOptions)
            ) : (
              <Select
                style={{ minWidth: '80px', margin: '5px' }}
                dropdownStyle={{ minWidth: '180px' }}
                disabled={isEverDeployed}
                options={calculateOptions}
              />
            )}
          </Form.Item>
          {/*<span>of</span>*/}
          <Form.Item noStyle name={['definition', 'occurrence_type']}>
            {/*{isView ? (*/}
            {/*  renderItemForViewMode(*/}
            {/*    form,*/}
            {/*    'occurrence_type',*/}
            {/*    getOccurrenceTypeOptions(EMetricType.SESSIONS),*/}
            {/*  )*/}
            {/*) : (*/}
            {/*  <Select*/}
            {/*    style={{ display: 'none' }}*/}
            {/*    options={getOccurrenceTypeOptions(EMetricType.SESSIONS)}*/}
            {/*  />*/}
            {/*)}*/}
            <Select
              style={{ display: 'none' }}
              options={getOccurrenceTypeOptions(EMetricType.SESSIONS)}
            />
          </Form.Item>
          {/* start of pairing */}
          <span>of</span>
          <Form.Item noStyle name={['definition', 'pairing_logic']}>
            {isView ? (
              renderItemForViewMode(form, 'pairing_logic', pairingLogicOptions)
            ) : (
              <Select
                options={pairingLogicOptions}
                style={{ minWidth: '140px', margin: '0 5px' }}
              />
            )}
          </Form.Item>
          <Form.Item noStyle dependencies={[['definition', 'pairing_logic']]}>
            {({ getFieldValue }) => {
              const pairingLogic = getFieldValue(['definition', 'pairing_logic']);
              let text;
              if (pairingLogic === EPairType.FIRST_FIRST) {
                text = 'first';
              }
              if (pairingLogic === EPairType.LAST_FIRST) {
                text = 'last';
              }
              const title = text ? (
                <span>
                  {`Pair the ${text}`}
                  <span style={{ fontStyle: 'italic' }}> initial event </span>
                  and the first
                  <span style={{ fontStyle: 'italic' }}> complete event </span>
                </span>
              ) : null;
              return (
                <Tooltip title={title}>
                  <InfoCircleOutlined
                    style={{ opacity: '0.4', fontSize: '18px', marginRight: 8 }}
                  />
                </Tooltip>
              );
            }}
          </Form.Item>
          {/* end of pairing */}
          {/*<Tooltip title={tooltip}>*/}
          {/*  <InfoCircleOutlined style={{ opacity: '0.4', fontSize: '18px', marginRight: 5 }} />*/}
          {/*</Tooltip>*/}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>within</span>
            {isView ? (
              renderItemForViewMode(form, 'within_range')
            ) : (
              <Form.Item
                name={['definition', 'within_range']}
                dependencies={[['definition', 'within_unit']]}
                validateStatus={rangeStatus}
                className={styles.withinRange}
                style={{ marginBottom: '0' }}
                rules={
                  [
                    enableRequired && { required: true, message: 'Required!' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const withinUnit = getFieldValue(['definition', 'within_unit']);
                        return checkSuccessConversionWindow(withinUnit, value, 6);
                      },
                    }),
                  ].filter(Boolean) as ComponentProps<typeof Form.Item>['rules']
                }
              >
                <InputNumber style={{ width: '70px', margin: '0 5px' }} min={1} />
              </Form.Item>
            )}
            <Form.Item noStyle name={['definition', 'within_unit']}>
              {isView ? (
                renderItemForViewMode(form, 'within_unit', withinUnitOptions)
              ) : (
                <Select options={withinUnitOptions} style={{ width: '100px' }} />
              )}
            </Form.Item>
          </div>
        </div>
        <MetricValueIndicator mode={formMode} form={form} />
        <WithoutEventMetric form={form} isView={isView} />
        <PropogateDimension eventIds={eventIds} form={form} isView={isView} type={'Metric'} />
      </div>
    );
  }

  if (metricType === EMetricTypes.SINGLE_EVENT) {
    return (
      <div style={{ paddingLeft: '28px' }}>
        <div className={styles.flexAlignCenter}>
          <span>Calculate the</span>
          <Form.Item
            name={['definition', 'calculation']}
            required={false}
            rules={[{ required: true }]}
            noStyle
          >
            {isView ? (
              renderItemForViewMode(form, 'calculation', CalculateTypeOptions)
            ) : (
              <Select
                style={{ minWidth: '80px', margin: '0 8px', width: 'fit-content' }}
                dropdownStyle={{ minWidth: '130px' }}
                disabled={isEverDeployed}
                options={CalculateTypeOptions}
              />
            )}
          </Form.Item>
          <span>of</span>
          {calculation !== 'count' ? (
            <>
              {isView ? (
                renderItemForViewMode(form, 'attribute')
              ) : (
                <div style={{ width: '180px', margin: '0 5px' }}>
                  <Form.Item
                    name={['definition', 'attribute']}
                    required={false}
                    validateStatus={attributeStatus}
                    rules={enableRequired ? [{ required: true, message: 'Required!' }] : []}
                    style={{ marginBottom: 0 }}
                  >
                    <AttributeSelect
                      disabled={!singleEventName}
                      eventId={singleEventId}
                      eventName={singleEventName}
                      interval={last14days}
                      attributeType={attributeType}
                      onAttributeTypeChange={(attributeType) =>
                        form.setFieldValue(['definition', 'attribute_type'], attributeType)
                      }
                    />
                  </Form.Item>
                </div>
              )}
              <Form.Item
                name={['definition', 'attribute_type']}
                dependencies={[['definition', 'attribute']]}
                style={{ display: 'none' }}
              />
              <span style={{ margin: '0 8px' }}>for</span>
            </>
          ) : null}
          <span
            style={{
              margin: '0 5px',
              fontWeight: 500,
              color: `${singleEventName ? '#000' : 'rgba(0,0,0,.45)'}`,
            }}
          >
            {singleEventName || 'Event'}
          </span>
        </div>
        {showNeedUnitCheckBox && (
          <div style={{ margin: '16px 0' }}>
            <Form.Item
              name={['definition', 'unit']}
              style={isView && !unit ? { display: 'none' } : { marginBottom: 0 }}
              required={false}
              validateStatus={unitStatus}
              rules={enableRequired ? [{ required: true, message: 'Required!' }] : []}
            >
              <UnitSelection disabled={isView} />
            </Form.Item>
          </div>
        )}
        <MetricValueIndicator mode={formMode} form={form} />
      </div>
    );
  }

  if (metricType === EMetricTypes.RATIO_METRIC) {
    return (
      <div style={{ paddingLeft: '28px' }}>
        <div style={{ margin: '16px 0' }}>
          <Form.Item
            name={['definition', 'unit']}
            style={isView && !unit ? { display: 'none' } : { marginBottom: 0 }}
            required={false}
            validateStatus={unitStatus}
            rules={enableRequired ? [{ required: true, message: 'Required!' }] : []}
          >
            <UnitSelection disabled={isView} />
          </Form.Item>
        </div>
        <MetricValueIndicator mode={formMode} form={form} />
      </div>
    );
  }

  return null;
};

export default MetricDefinition;
